// Los objetivos del cliente, agregados desde sus líneas de control. PURO: sin React y sin i18n
// adentro, igual que `panelFormatters` — el copy lo pone el tile que lo consume.
//
// Vive en el frontend y no en el agregado a propósito: el backend YA manda el objetivo de cada
// línea (`lines[].goal`, resuelto en dos queries) y sumarlos es contar, no calcular. Pedirle al
// backend un segundo total sería una segunda fuente para el mismo número — el día que las dos no
// coincidan (una filtra líneas sin dato fresco y la otra no) el usuario ve dos verdades en la misma
// pantalla.

/**
 * Un objetivo cuenta como "en ritmo" sólo si el backend lo dijo. `on_track: null` es "no sé"
 * (la línea todavía no tiene `GoalInformation`: el sync no llegó), NO "está atrasado" —
 * mezclarlos pinta de rojo a un cliente recién conectado.
 */
export const GOAL_ON_TRACK = 'ON_TRACK';
export const GOAL_OFF_TRACK = 'OFF_TRACK';
export const GOAL_UNKNOWN = 'UNKNOWN';

function stateOf(goal) {
    if (goal.on_track === true) return GOAL_ON_TRACK;
    if (goal.on_track === false) return GOAL_OFF_TRACK;
    return GOAL_UNKNOWN;
}

// Primero lo accionable: lo que se salió de ritmo, después lo que todavía no se puede juzgar, y al
// final lo que va bien. El tile muestra las dos primeras filas, así que este orden decide QUÉ dos
// objetivos se ven de un vistazo — y un objetivo cumplido no es la noticia.
const STATE_ORDER = {[GOAL_OFF_TRACK]: 0, [GOAL_UNKNOWN]: 1, [GOAL_ON_TRACK]: 2};

/**
 * Resumen de los objetivos de un cliente a partir de `panel.lines`.
 *
 * Devuelve `{defined, evaluated, onTrack, offTrack, items}`:
 * - `defined`  — cuántas líneas tienen un objetivo cargado (0 ⇒ el tile ofrece definir uno).
 * - `evaluated`— de esas, cuántas tienen veredicto. Es el DENOMINADOR del titular: decir
 *                "1 de 3 en ritmo" cuando dos ni se evaluaron acusa un problema que nadie verificó.
 * - `onTrack` / `offTrack` — el reparto de las evaluadas.
 * - `items`    — una entrada por objetivo (`lineId`, `lineName`, `state` y el propio `goal`), ya
 *                ordenada por lo que más conviene mirar.
 *
 * Ignora las líneas sin objetivo en silencio: no tener uno no es un incumplimiento, y contarlas
 * como "fuera de ritmo" convertiría el tile en una acusación permanente.
 */
export function goalsSummary(lines) {
    const items = (lines || [])
        .filter((line) => line && line.goal && line.goal.defined)
        .map((line) => ({
            lineId: line.id,
            lineName: line.name,
            goal: line.goal,
            state: stateOf(line.goal),
        }))
        .sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state]);

    const onTrack = items.filter((item) => item.state === GOAL_ON_TRACK).length;
    const offTrack = items.filter((item) => item.state === GOAL_OFF_TRACK).length;

    return {
        defined: items.length,
        evaluated: onTrack + offTrack,
        onTrack,
        offTrack,
        items,
    };
}

/**
 * Color del tile: oro si algún objetivo se salió de ritmo, verde si todos los evaluados van bien,
 * `null` (gris) si no hay NADA evaluado.
 *
 * El gris no es un estado degradado: es la respuesta honesta cuando todavía no hay con qué opinar.
 * Misma regla que la barra del Listado de Clientes v2, donde un booleano pintaba de rojo todo lo
 * que no fuera verde —incluido el "todavía no sé"—.
 */
export function goalsSeverity(summary) {
    if (!summary || !summary.evaluated) return null;
    return summary.offTrack > 0 ? 'gold' : 'green';
}
